<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Download, X, Check, AlertTriangle, RotateCw, ChevronDown, ChevronUp } from 'lucide-vue-next'
import { useDownloadStore } from '@/stores'
import type { DownloadTask } from '@/stores/modules/download'
import commonUtil from '@/utils/commonUtil'
import DownloadHistoryTable from '@/components/DownloadHistoryTable.vue'
import TableTooltip from '@/components/TableTooltip.vue'
import { useTableTooltip } from '@/utils/useTableTooltip'

const {
  hoveredTooltipText,
  showTableTooltip,
  tableTooltipX,
  tableTooltipY,
  handleTableTooltipMouseMove,
  handleCellMouseEnter,
  handleCellMouseLeave,
  handleTooltipMouseEnter,
  handleTooltipMouseLeave,
} = useTableTooltip()

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const downloadStore = useDownloadStore()

// 최근 시작한 다운로드가 위로 오게 — Object.values는 삽입 순서를 보존하므로 뒤집기만 하면 됨.
// 이번 세션에서 시작된(진행 중/방금 끝난) 항목만 여기 보이고, 지난 이력은 아래
// DownloadHistoryTable(서버 조회)이 별도로 보여준다.
const downloadTaskList = computed(() => Object.values(downloadStore.tasks).reverse())

// 항상 봐야 하는 건 아니라 기본은 접어두고, 필요할 때만 펼친다.
const isHistoryExpanded = ref(false)

// 이력 표는 모달(v-if)과 함께 열 때마다 새로 마운트되며 최신 이력을 직접 불러온다(DownloadHistoryTable 의 onMounted).
// 여기서 다시 refresh 하면 같은 요청이 두 번 나간다.
watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    // 알림 클릭으로 열린 경우(openManagerWithHistory) 이력을 펼친 채로 보여준다 —
    // 새로고침 후엔 이번 세션 목록(downloadTaskList)에서 사라져 있을 수 있어서다.
    if (downloadStore.openManagerWithHistoryExpanded) {
      isHistoryExpanded.value = true
      downloadStore.openManagerWithHistoryExpanded = false
    }
  },
)

function downloadProgressPercent(task: DownloadTask): number {
  if (!task.totalBytes) return 0
  return Math.min(100, Math.round((task.receivedBytes / task.totalBytes) * 100))
}

function handleDownloadTaskAction(task: DownloadTask) {
  if (task.status === 'downloading' || task.status === 'retrying') {
    downloadStore.cancelDownload(task.dataNo, task.fileNo)
  } else {
    downloadStore.clearTask(task.dataNo, task.fileNo)
  }
}

function resumeTask(task: DownloadTask) {
  downloadStore.resumeDownload(task.dataNo, task.fileNo)
}

// 항목 우측의 취소/지우기 아이콘 버튼 — 텍스트 말줄임과 무관하게 항상 떠야 하므로
// (overflow 여부로 자동 판단하는 handleCellMouseEnter 대신) 직접 표시한다.
function handleTaskActionEnter(e: MouseEvent, task: DownloadTask) {
  handleTableTooltipMouseMove(e)
  hoveredTooltipText.value = task.status === 'downloading' || task.status === 'retrying' ? '취소' : '지우기'
  showTableTooltip.value = true
}

function handleTaskActionLeave() {
  showTableTooltip.value = false
  hoveredTooltipText.value = ''
}

function clearFinishedDownloads() {
  downloadTaskList.value.forEach((t) => {
    if (t.status !== 'downloading' && t.status !== 'retrying') downloadStore.clearTask(t.dataNo, t.fileNo)
  })
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[5000] flex items-center justify-center p-4 overflow-y-auto"
    >
      <!-- 배경 오버레이 -->
      <div class="fixed inset-0 bg-slate-900/65 backdrop-blur-sm" />

      <!-- 모달 컨테이너 -->
      <div
        class="relative bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden border border-slate-100 z-10"
      >
        <!-- 헤더 (Dark Navy) -->
        <div
          class="px-6 py-4 text-white flex items-center justify-between shadow-sm relative overflow-hidden border-b border-slate-800"
          style="background-color: var(--surface-dark, #0f1a3a)"
        >
          <div class="flex items-center gap-2.5">
            <Download class="w-5 h-5 text-blue-400 shrink-0" />
            <h3 class="font-bold text-base text-white tracking-tight">다운로드 관리</h3>
          </div>
          <button
            @click="emit('close')"
            class="text-slate-300 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- 다운로드 목록 (이번 세션에서 시작한 진행 중/완료 항목) -->
        <div class="px-6 pt-4 pb-1 flex items-center justify-between">
          <p class="text-xs font-semibold text-slate-400">다운로드 목록</p>
          <button
            v-if="downloadTaskList.some((t) => t.status !== 'downloading' && t.status !== 'retrying')"
            class="text-xs font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
            @click="clearFinishedDownloads"
          >
            완료 항목 지우기
          </button>
        </div>
        <div class="max-h-[300px] overflow-y-auto">
          <p v-if="downloadTaskList.length === 0" class="px-6 py-10 text-center text-sm text-slate-400">
            다운로드 내역이 없습니다
          </p>

          <div
            v-for="task in downloadTaskList"
            :key="task.key"
            class="px-6 py-4 border-b border-slate-100 last:border-b-0"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <p
                  class="text-sm font-semibold text-slate-800 truncate"
                  @mouseenter="handleCellMouseEnter($event, task.fileName)"
                  @mouseleave="handleCellMouseLeave"
                  @mousemove="handleTableTooltipMouseMove"
                >
                  {{ task.fileName }}
                </p>
                <p class="text-xs text-slate-400 mt-0.5">
                  {{ task.fileType }}<span v-if="task.totalBytes"> · {{ commonUtil.formatBytes(task.totalBytes, 1) }}</span>
                </p>
              </div>

              <button
                class="flex-shrink-0 h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                @mouseenter="handleTaskActionEnter($event, task)"
                @mouseleave="handleTaskActionLeave"
                @mousemove="handleTableTooltipMouseMove"
                @click="handleDownloadTaskAction(task)"
              >
                <X class="h-3.5 w-3.5" />
              </button>
            </div>

            <!-- 다운로드 중 / 재시도 중: 진행률 바 -->
            <div v-if="task.status === 'downloading' || task.status === 'retrying'" class="mt-2.5">
              <div class="h-1.5 rounded-full overflow-hidden bg-slate-100">
                <div
                  class="h-full rounded-full transition-all"
                  :class="task.status === 'retrying' ? 'bg-amber-500' : 'bg-blue-600'"
                  :style="{ width: downloadProgressPercent(task) + '%' }"
                />
              </div>
              <p v-if="task.status === 'retrying'" class="text-xs font-semibold text-amber-600 mt-1 flex items-center gap-1">
                <RotateCw class="h-3 w-3" /> 일시적 오류, 재시도 중… ({{ task.retryAttempt }}/4) · {{ downloadProgressPercent(task) }}%
              </p>
              <p v-else class="text-xs font-semibold text-blue-600 mt-1">{{ downloadProgressPercent(task) }}%</p>
            </div>

            <!-- 완료 -->
            <p v-else-if="task.status === 'done'" class="text-xs mt-1.5 flex items-center gap-1 text-emerald-600 font-medium">
              <Check class="h-3 w-3" /> 완료
            </p>

            <!-- 실패 -->
            <div v-else-if="task.status === 'error'" class="mt-1.5 flex items-center justify-between gap-2">
              <p class="text-xs flex items-center gap-1 text-rose-500 font-medium min-w-0">
                <AlertTriangle class="h-3 w-3 shrink-0" />
                <span class="truncate" v-text-tooltip.overflow="task.errorMessage || '다운로드 실패'">{{ task.errorMessage || '다운로드 실패' }}</span>
              </p>
              <button
                v-if="task.resumable"
                class="shrink-0 text-xs font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                @click="resumeTask(task)"
              >
                이어받기
              </button>
            </div>

            <!-- 취소됨 -->
            <p v-else-if="task.status === 'cancelled'" class="text-xs mt-1.5 text-slate-400">취소됨</p>
          </div>
        </div>

        <!-- 다운로드 이력 (서버 조회, 로그인 사용자 본인 전체) -->
        <div class="border-t border-slate-100">
          <button
            type="button"
            class="w-full flex items-center justify-between px-6 py-4 cursor-pointer"
            @click="isHistoryExpanded = !isHistoryExpanded"
          >
            <span class="text-xs font-semibold text-slate-400">다운로드 이력</span>
            <ChevronUp v-if="isHistoryExpanded" :size="14" class="text-slate-400" />
            <ChevronDown v-else :size="14" class="text-slate-400" />
          </button>
          <div v-show="isHistoryExpanded" class="max-h-[40vh] overflow-y-auto px-2 pb-2">
            <DownloadHistoryTable />
          </div>
        </div>
      </div>
    </div>

    <TableTooltip
      :show="showTableTooltip"
      :text="hoveredTooltipText"
      :x="tableTooltipX"
      :y="tableTooltipY"
      @mouseenter="handleTooltipMouseEnter"
      @mouseleave="handleTooltipMouseLeave"
    />
  </Teleport>
</template>
